import { Injectable, signal, effect, computed } from '@angular/core';

// Tipos disponibles
export type Climate = 'light' | 'dark';
export type Palette = 'default' | 'minimal' | 'desert' | 'forest' | 'snow' | 'violet';

// Interfaz del estado del tema
export interface ThemeState {
    climate: Climate;
    palette: Palette;
}

// Opciones de paletas premium
export const PALETTE_OPTIONS: { id: Palette; label: string; icon: string }[] = [
    { id: 'default', label: 'NTLink Prime', icon: 'auto_awesome' },
    { id: 'violet', label: 'Amethyst Violet', icon: 'diamond' },
    { id: 'desert', label: 'Desert Gold', icon: 'wb_sunny' },
    { id: 'forest', label: 'Emerald Forest', icon: 'park' },
    { id: 'snow', label: 'Snow Arctic', icon: 'ac_unit' },
    { id: 'minimal', label: 'Minimal Essence', icon: 'contrast' },
];

const STORAGE_KEY = 'ntlink-theme-storage';

@Injectable({
    providedIn: 'root'
})
export class ThemeService {
    private _climate = signal<Climate>('light');
    private _palette = signal<Palette>('default');

    readonly climate = computed(() => this._climate());
    readonly palette = computed(() => this._palette());
    readonly isDark = computed(() => this._climate() === 'dark');

    constructor() {
        this.loadFromStorage();

        effect(() => {
            this.applyThemeToDOM();
        });

        effect(() => {
            this.saveToStorage();
        });
    }

    setClimate(climate: Climate): void {
        this._climate.set(climate);
    }

    setPalette(palette: Palette): void {
        this._palette.set(palette);
    }

    toggleClimate(): void {
        this._climate.update(current => current === 'light' ? 'dark' : 'light');
    }

    nextPalette(): void {
        const current = this._palette();
        const index = PALETTE_OPTIONS.findIndex(p => p.id === current);
        const nextIndex = (index + 1) % PALETTE_OPTIONS.length;
        this._palette.set(PALETTE_OPTIONS[nextIndex].id);
    }

    updateTheme(state: Partial<ThemeState>): void {
        if (state.climate) this._climate.set(state.climate);
        if (state.palette) this._palette.set(state.palette);
    }

    private applyThemeToDOM(): void {
        const root = document.documentElement;
        const climate = this._climate();
        const palette = this._palette();

        // Aplicar atributos para selectores CSS [data-*]
        root.setAttribute('data-climate', climate);
        root.setAttribute('data-palette', palette);

        // Actualizar clases en el body para selectores .palette-* y .climate-*
        document.body.className = `theme-root climate-${climate} palette-${palette} ${climate === 'dark' ? 'dark-mode' : 'light-mode'}`;

        // Añadir una pequeña clase de transición para suavizar el cambio
        document.body.classList.add('theme-transitioning');
        setTimeout(() => {
            document.body.classList.remove('theme-transitioning');
        }, 300);
    }

    private saveToStorage(): void {
        const state: ThemeState = {
            climate: this._climate(),
            palette: this._palette()
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    }

    private loadFromStorage(): void {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                const state: ThemeState = JSON.parse(stored);
                if (state.climate) this._climate.set(state.climate);
                if (state.palette) this._palette.set(state.palette);
            }
        } catch (e) {
            console.warn('Error loading theme from storage:', e);
        }
    }
}
